import { onScopeDispose, Ref, watch } from 'vue'

import { ConfirmQueueIncident } from '@collector/desktop-feature-incidents-queue-core'
import type { RabbitErrorMessage } from '@collector/queue-gateway-shared-types'

import { RabbitMQError } from './RabbitMQError'
import { IncidentUpdate } from './types'

/**
 * Wire CQRS confirm: watch incidentsUpdate query cache + return handlers for
 * EventIncidentsQueue.setConfirmQueueIncident and the error topic.
 *
 * Correlation state is scoped to this composable instance so a remounted Relation
 * cannot resolve/reject waiters belonging to a previous session.
 */
export function useConfirmIncident(
  incidentsUpdate: Ref<IncidentUpdate[] | undefined>,
): {
  onQueueRabbitConfirmIncident: ConfirmQueueIncident
  onQueueRabbitErrorMessage: (message: RabbitErrorMessage) => void
  acknowledgeIncidentUuids: (uuids: string[]) => void
} {
  const uuidToPromiseMap = new Map<
    string,
    { resolve: () => void; reject: (error: Error) => void }
  >()
  const confirmedUuids = new Set<string>()
  const errorUuids = new Map<string, Error>()

  function resolveUuid(uuid: string): void {
    const waiter = uuidToPromiseMap.get(uuid)
    if (waiter) {
      waiter.resolve()
      uuidToPromiseMap.delete(uuid)
    } else {
      confirmedUuids.add(uuid)
    }
  }

  /** Resolve waiters for UUIDs that did not land in incidentsUpdate (e.g. cold-sync overlap). */
  function acknowledgeIncidentUuids(uuids: string[]): void {
    for (const uuid of uuids) {
      resolveUuid(uuid)
    }
  }

  function onQueueRabbitErrorMessage(message: RabbitErrorMessage): void {
    const { uuid } = message
    const { code, message: errorMessage } = message.data
    const error = new RabbitMQError(errorMessage, code)

    const waiter = uuidToPromiseMap.get(uuid)
    if (waiter) {
      waiter.reject(error)
      uuidToPromiseMap.delete(uuid)
    } else {
      errorUuids.set(uuid, error)
    }
  }

  const onQueueRabbitConfirmIncident: ConfirmQueueIncident = (
    uuid,
    resolve,
    reject,
  ) => {
    if (confirmedUuids.has(uuid)) {
      confirmedUuids.delete(uuid)
      resolve()
    } else if (errorUuids.has(uuid)) {
      const error = errorUuids.get(uuid)!
      errorUuids.delete(uuid)
      reject(error)
    } else {
      uuidToPromiseMap.set(uuid, { resolve, reject })
    }
  }

  // confirming incident scenarios:
  // 1. confirm registered first, then incidentsUpdate arrives with the same uuid
  // 2. incidentsUpdate arrives first (slow HTTP), then confirm is registered
  watch(incidentsUpdate, (newVal) => {
    if (!newVal) return
    for (const { uuid } of newVal) {
      if (!import.meta.env.DEV) {
        // eslint-disable-next-line
        console.debug(
          `[queueRabbitClient] message with uuid ${uuid} was received.`,
        )
      }
      resolveUuid(uuid)
    }
  })

  onScopeDispose(() => {
    for (const { reject } of uuidToPromiseMap.values()) {
      reject(new Error('Relation confirm session disposed'))
    }
    uuidToPromiseMap.clear()
    confirmedUuids.clear()
    errorUuids.clear()
  })

  return {
    onQueueRabbitConfirmIncident,
    onQueueRabbitErrorMessage,
    acknowledgeIncidentUuids,
  }
}
